import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';

interface BaseProps {
  label: string;
  required?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
}

/**
 * UI test 01/10/2026 (WCAG 1.3.1 / 3.3.2): ô bắt buộc mang `aria-required` (dấu * chỉ là hình — ẩn với trình đọc màn hình),
 * gợi ý và lỗi đều nối vào ô bằng `aria-describedby` (trước chỉ nối lỗi của TextField). Ô có lỗi mới mang `aria-invalid`.
 */
function a11y(fid: string, required: boolean | undefined, hint: string | undefined, error: string | undefined) {
  return {
    'aria-invalid': error ? true : undefined,
    'aria-required': required ? true : undefined,
    'aria-describedby': error ? `${fid}-err` : hint ? `${fid}-hint` : undefined,
  } as const;
}

function Label({ fid, label, required }: { fid: string; label: string; required: boolean | undefined }) {
  return (
    <label htmlFor={fid}>
      {label}
      {required ? (
        <span className="req" aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  );
}

function Help({ fid, hint, error }: { fid: string; hint: string | undefined; error: string | undefined }) {
  return error ? (
    <span className="err" id={`${fid}-err`}>
      {error}
    </span>
  ) : hint ? (
    <span className="hint" id={`${fid}-hint`}>
      {hint}
    </span>
  ) : null;
}

export function TextField({
  label,
  required,
  hint,
  error,
  id,
  ...rest
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={`kx-field ${error ? 'invalid' : ''}`}>
      <Label fid={fid} label={label} required={required} />
      <input id={fid} {...a11y(fid, required, hint, error)} {...rest} />
      <Help fid={fid} hint={hint} error={error} />
    </div>
  );
}

export function SelectField({
  label,
  required,
  hint,
  error,
  id,
  children,
  ...rest
}: BaseProps & SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={`kx-field ${error ? 'invalid' : ''}`}>
      <Label fid={fid} label={label} required={required} />
      <select id={fid} {...a11y(fid, required, hint, error)} {...rest}>
        {children}
      </select>
      <Help fid={fid} hint={hint} error={error} />
    </div>
  );
}

export function TextArea({
  label,
  required,
  hint,
  error,
  id,
  ...rest
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={`kx-field ${error ? 'invalid' : ''}`}>
      <Label fid={fid} label={label} required={required} />
      <textarea id={fid} {...a11y(fid, required, hint, error)} {...rest} />
      <Help fid={fid} hint={hint} error={error} />
    </div>
  );
}
